/** @jsx jsx */
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  cloneElement,
  useContext,
  Fragment as _,
} from 'react';
import { css, jsx } from '@emotion/core';
import { CALCULATED_LINE_HEIGHT_OFFSET } from 'styles/variables';
import Close from 'assets/close.svg';
import { InteractionContext } from 'components/Layouts/ContainerLogical';
import { Node, Text } from 'slate';
import html from './serializer';

const findUl = node => {
  if (node === document.body) {
    return;
  }

  if (node.parentNode.tagName.toLowerCase() === 'ul') {
    return node.parentNode;
  }

  return findUl(node.parentNode);
};

const Indent = ({ type, attributes, children }) => {
  const indent: any = useRef(null);

  useLayoutEffect(() => {
    const foundUl = findUl(indent.current.parentNode);

    let closestLi;
    if (foundUl) {
      closestLi = indent.current.closest('li');
      if (closestLi) {
        closestLi.classList.add('indent');
      }
    } else {
      indent.current.classList.add('indent-alt');
    }

    return () => {
      closestLi?.classList.remove('indent');
      indent.current.classList.remove('indent-alt');
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      const foundUl = findUl(indent.current.parentNode);

      if (!foundUl && indent.current.classList.contains('indent-alt')) {
        console.log('kill it with fire !!!');
      }
    });
  }, []);

  return (
    <span ref={indent} data-type={type} {...attributes}>
      {children}
    </span>
  );
};

const Lang = ({ type, lang, attributes, children }) => {
  const langRef: any = useRef(null);

  if (!lang) {
    return children;
  }

  return (
    <span>
      <span
        ref={langRef}
        className="lang-bracket"
        data-type={type}
        {...attributes}
      >
        {children}
      </span>
      {lang && (
        <sup
          style={{
            fontSize: 11,
          }}
        >
          *{lang}
        </sup>
      )}
    </span>
  );
};

const renderMark = (props, editor, next) => {
  const { children, mark, attributes } = props;
  const { type, data } = mark;

  switch (type) {
    case 'bold':
      return <strong {...attributes}>{children}</strong>;
    case 'italic':
      return <em {...attributes}>{children}</em>;
    case 'underline':
      return <u {...attributes}>{children}</u>;

    case 'fontFamily':
      const fontFamily = data.get('fontFamily');

      return (
        <span style={{ fontFamily }} data-type={type} {...attributes}>
          {children}
        </span>
      );

    case 'fontSize':
      const fontSize = parseInt(data.get('fontSize'), 10);
      const calculatedLineHeight =
        fontSize <= 18 ? 1.5 : fontSize * CALCULATED_LINE_HEIGHT_OFFSET;

      return (
        <span
          style={{
            fontSize,
            lineHeight: `${calculatedLineHeight}px`,
          }}
          data-type={type}
          {...attributes}
        >
          {children}
        </span>
      );

    case 'color':
      return (
        <span
          style={{ color: data.get('color') }}
          data-type={type}
          {...attributes}
        >
          {children}
        </span>
      );

    case 'lang':
      const lang = data.get('lang');

      // if (!children?.props?.text) {
      //   return null;
      // }
      //

      return (
        <Lang type={type} lang={lang} {...attributes}>
          {children}
        </Lang>
      );

    case 'span': {
      return children;
    }

    case 'variable': {
      const interactionContext = useContext(InteractionContext);

      const { node } = props;
      const varName = data.get('varName');
      const { text } = props;

      const [localColor, setLocalColor] = useState<string | undefined>();
      const variable = useRef<HTMLSpanElement>();

      const widgetColor = localColor || interactionContext?.textColor;

      useLayoutEffect(() => {
        if (!variable.current) {
          return;
        }

        //
        const chameleonText = variable.current.closest('.chameleon-text p');
        let spanWithColor = variable.current.closest('[data-type="color"]');
        //

        if (spanWithColor && !chameleonText?.contains(spanWithColor)) {
          spanWithColor = null;
        }

        const chameleonTextComputedStyle =
          chameleonText && getComputedStyle(chameleonText);
        const spanWithColorComputedStyle =
          spanWithColor && getComputedStyle(spanWithColor);

        spanWithColorComputedStyle?.color
          ? setLocalColor(spanWithColorComputedStyle?.color)
          : setLocalColor(chameleonTextComputedStyle?.color);
      });

      useEffect(() => {
        const onKeyAction = e => {
          if (variable?.current?.innerText !== text) {
            editor.moveToRangeOfNode(node).delete();
          }
        };

        window.addEventListener('keydown', onKeyAction);

        return () => {
          window.removeEventListener('keydown', onKeyAction);
        };
      }, []);

      return (
        <span
          ref={variable}
          className="variable"
          data-type={type}
          //
          data-varname={varName || text}
          {...attributes}
          style={{
            position: 'relative',
            display: 'inline-block',
            padding: '0 25px 0 10px',
            margin: '0 3px',
            borderRadius: 5,
            boxShadow: `0 0 0 1px ${widgetColor}`,
          }}
          contentEditable="false"
          suppressContentEditableWarning
          onClick={e => {
            //
            const target = e.target as HTMLElement;
            const dataType = target.getAttribute('data-type');

            if (!dataType) {
              // select whole text ( for deletion )
              editor.moveToStartOfText().moveFocusForward(text.length);
              return;
            }

            if (!variable?.current) {
              return;
            }

            const { left, right, width } = target.getBoundingClientRect();
            const middle = left + width / 2;

            const leftOrRightEdge = e.clientX < middle ? 'left' : 'right';

            const pParent = variable.current?.closest('p');

            if (leftOrRightEdge === 'left') {
              if (pParent?.firstChild?.contains(variable.current)) {
                // console.log('is first elem. adding buffer');
                addEmptyAndRemoveMarks();

                const { document } = html.deserialize('<span> </span>');
                editor.insertFragment(document);

                // we lose focus / caret / cursor. this doesn't help
                // maybe we need to manually force a selection ?
                editor.focus();
              }
              return;
            }

            if (pParent?.lastChild?.contains(variable?.current)) {
              // console.log('is last elem. adding buffer');
              addEmptyAndRemoveMarks();
              editor.moveForward();
            }

            function addEmptyAndRemoveMarks() {
              editor.insertText(' ').moveFocusBackward(1);

              const selection = editor.value;

              selection.marks.toJS().forEach(m => {
                editor.removeMark(m);
              });
            }
          }}
        >
          {children}
          <span
            style={{
              display: 'flex',
              position: 'absolute',
              justifyContent: 'center',
              alignItems: 'center',

              right: 5,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={`${13 * 0.7}px`}
              height={`${12 * 0.7}px`}
              viewBox="0 0 13 12"
              version="1.1"
              css={css({
                cursor: 'pointer',
                ':hover': {
                  opacity: 0.8,
                },
              })}
              onClick={() => {
                try {
                  editor.moveToRangeOfNode(node).delete();
                } catch (err) {}
              }}
            >
              <g
                stroke="none"
                strokeWidth="1"
                fill={widgetColor}
                fillRule="evenodd"
              >
                <g
                  transform="translate(-1332.000000, -166.000000)"
                  fill={widgetColor}
                >
                  <g transform="translate(1332.000000, 166.000000)">
                    <polygon
                      transform="translate(6.500000, 6.000000) rotate(-45.000000) translate(-6.500000, -6.000000) "
                      points="-0.7625 5.1375 13.75 5.25 13.7625 6.8625 -0.75 6.75"
                    />
                    <polygon
                      transform="translate(6.500000, 6.000000) rotate(45.000000) translate(-6.500000, -6.000000) "
                      points="-0.75 5.25 13.7625 5.1375 13.75 6.75 -0.7625 6.8625"
                    />
                  </g>
                </g>
              </g>
            </svg>
          </span>
        </span>
      );
    }

    case 'indent': {
      return (
        <Indent type={type} attributes={attributes}>
          {children}
        </Indent>
      );
    }

    case 'superscript': {
      return (
        <sup data-type={type} {...attributes}>
          {children}
        </sup>
      );
    }

    case 'subscript': {
      return (
        <sub data-type={type} {...attributes}>
          {children}
        </sub>
      );
    }

    default:
      return next();
  }
};

export default renderMark;
